import {useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Switch} from '@/components/ui/switch'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {HelpIcon} from '@/components/help-icon'
import {NumberField} from '@/components/wizard-fields'
import {PaquetesMultiSelect, type PaqueteFiltro} from '@/components/bateria/cola/paquetes-multi-select'
import {useResumenDeTodo} from '@/hooks/bateria/use-bateria-cola'
import {ESTADOS_FILTRO, type EstadoFiltro, type FiltrosCola, type RevisarCola} from '@/lib/cola-filtros'
import {cn} from '@/lib/utils'

/** 80 y no 85: con 85 (`CONFIANZA_CIERTA`) el filtro no quitaría nada de la cola de dudas. */
const PCT_POR_DEFECTO = 80

/** Conserva el orden canónico: el chip enumera lo elegido y debe leerse igual sobre el mismo filtro. */
function alternarEstado(estados: EstadoFiltro[], estado: EstadoFiltro): EstadoFiltro[] {
  const siguientes = estados.includes(estado) ? estados.filter((otro) => otro !== estado) : [...estados, estado]
  return ESTADOS_FILTRO.filter((conocido) => siguientes.includes(conocido))
}

type Tono = 'neutral' | 'ambar' | 'apagado'

/** La ✕ y el clic no conviven: serían botones anidados, HTML inválido. */
function Chip({
  testId,
  texto,
  tono = 'neutral',
  ayuda,
  onClick,
  disabled,
  onQuitar,
  quitarLabel,
}: {
  testId: string
  texto: string
  tono?: Tono
  ayuda?: ReactNode
  onClick?: () => void
  disabled?: boolean
  onQuitar?: () => void
  quitarLabel?: string
}) {
  const {t} = useTranslation()
  const interactivo = onClick !== undefined || disabled === true
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs',
        tono === 'ambar' && 'notice-amber border-transparent font-medium',
        tono === 'neutral' && 'border-input text-muted-foreground',
        tono === 'apagado' && 'border-dashed border-input text-muted-foreground'
      )}>
      {interactivo ? (
        <button
          type="button"
          data-testid={testId}
          disabled={disabled}
          onClick={onClick}
          className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-default">
          {texto}
        </button>
      ) : (
        <span data-testid={testId}>{texto}</span>
      )}
      {ayuda !== undefined && <HelpIcon label={t('common.help.iconLabelFor', {campo: texto})}>{ayuda}</HelpIcon>}
      {onQuitar !== undefined && (
        <button
          type="button"
          data-testid={`${testId}-quitar`}
          aria-label={quitarLabel}
          onClick={onQuitar}
          className="rounded-full text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50">
          ✕
        </button>
      )}
    </span>
  )
}

function OpcionRevisar({
  valor,
  actual,
  etiqueta,
  disabled,
  motivo,
  onElegir,
}: {
  valor: RevisarCola
  actual: RevisarCola
  etiqueta: string
  disabled?: boolean
  motivo?: string
  onElegir: (valor: RevisarCola) => void
}) {
  return (
    <label
      className={cn('flex items-start gap-2 text-sm', disabled ? 'text-muted-foreground' : 'text-foreground')}
      data-testid={`cola-filtros-revisar-${valor}`}>
      <input
        type="radio"
        name="cola-revisar"
        className="mt-1"
        checked={actual === valor}
        disabled={disabled}
        onChange={() => onElegir(valor)}
      />
      <span>
        {etiqueta}
        {disabled && motivo && <span className="block text-xs text-muted-foreground">{motivo}</span>}
      </span>
    </label>
  )
}

/** El chip de las dejadas está siempre, incluso en cero: una puerta que solo aparece tras usarla no es puerta. */
export function BarraDeFiltros({
  filtros,
  businessId,
  evaluacionId,
  pendientes,
  dejadas,
  sujeto,
  paquetes,
  onCambiar,
}: {
  filtros: FiltrosCola
  businessId: string
  evaluacionId: string
  /** Del lote, nunca de la cola filtrada. */
  pendientes: number
  dejadas: number
  /** Una persona, N personas o toda la evaluación. */
  sujeto: string
  paquetes: PaqueteFiltro[]
  onCambiar: (filtros: FiltrosCola) => void
}) {
  const {t} = useTranslation()
  const [abierto, setAbierto] = useState(filtros.abrirPanel)
  // El resumen de «todos los ítems» recorre el lote entero: se pide al abrir el panel por primera vez.
  const [panelUsado, setPanelUsado] = useState(filtros.abrirPanel)
  const [borrador, setBorrador] = useState(filtros)
  const [pct, setPct] = useState(String(Math.round((filtros.confianzaMax ?? PCT_POR_DEFECTO / 100) * 100)))
  const {data: resumenTodo} = useResumenDeTodo(businessId, evaluacionId, panelUsado)

  const soloPedido = filtros.solo !== null
  const personas = filtros.paqueteIds.length

  function abrirPanel(abre: boolean) {
    setAbierto(abre)
    if (!abre) return
    setPanelUsado(true)
    setBorrador(filtros)
    setPct(String(Math.round((filtros.confianzaMax ?? PCT_POR_DEFECTO / 100) * 100)))
  }

  function aplicar(cambio: FiltrosCola) {
    setAbierto(false)
    onCambiar(cambio)
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="cola-filtros">
      <Chip
        testId="cola-chip-personas"
        texto={
          personas === 0
            ? t('admin.bateria.cola.filtros.todoElLote')
            : personas === 1
              ? sujeto
              : t('admin.bateria.cola.filtros.personas', {n: personas})
        }
        onQuitar={personas > 0 ? () => onCambiar({...filtros, paqueteIds: []}) : undefined}
        quitarLabel={t('admin.bateria.cola.filtros.quitarPersonas')}
      />

      {filtros.revisar !== 'dejadas' && (
        <Chip
          testId="cola-chip-revisar"
          texto={
            filtros.revisar === 'todo'
              ? t('admin.bateria.cola.filtros.todosLosItems')
              : t('admin.bateria.cola.filtros.loQueFalta', {n: pendientes})
          }
        />
      )}

      <Chip
        testId="cola-chip-dejadas"
        tono={filtros.revisar === 'dejadas' ? 'ambar' : dejadas > 0 ? 'neutral' : 'apagado'}
        texto={
          dejadas > 0
            ? t('admin.bateria.cola.filtros.dejadas', {n: dejadas})
            : t('admin.bateria.cola.filtros.sinDejadas')
        }
        ayuda={dejadas === 0 ? t('admin.bateria.cola.filtros.queEsDejar') : undefined}
        disabled={dejadas === 0 || soloPedido || filtros.revisar === 'dejadas'}
        onClick={
          dejadas > 0 && !soloPedido && filtros.revisar !== 'dejadas'
            ? () => onCambiar({...filtros, revisar: 'dejadas', solo: null})
            : undefined
        }
      />

      {filtros.estados.length > 0 && (
        <Chip
          testId="cola-chip-estado"
          texto={t('admin.bateria.cola.filtros.estadoChip', {
            estados: filtros.estados
              .map((estado) => t(`admin.bateria.cola.filtros.panel.estadoOpcion.${estado}`))
              .join(' · '),
          })}
          onQuitar={() => onCambiar({...filtros, estados: []})}
          quitarLabel={t('admin.bateria.cola.filtros.quitarEstado')}
        />
      )}

      {filtros.confianzaMax !== null && (
        <Chip
          testId="cola-chip-confianza"
          texto={t('admin.bateria.cola.filtros.confianzaChip', {pct: Math.round(filtros.confianzaMax * 100)})}
          onQuitar={() => onCambiar({...filtros, confianzaMax: null})}
          quitarLabel={t('admin.bateria.cola.filtros.quitarConfianza')}
        />
      )}

      {filtros.solo !== null && (
        <Chip
          testId="cola-chip-solo"
          texto={t('admin.bateria.cola.filtros.puntosPedidos', {
            count: filtros.solo.split(',').filter((ref) => ref.trim() !== '').length,
          })}
          onQuitar={() => onCambiar({...filtros, solo: null})}
          quitarLabel={t('admin.bateria.cola.filtros.quitarSolo')}
        />
      )}

      <Popover open={abierto} onOpenChange={abrirPanel}>
        <PopoverTrigger asChild>
          <Button size="xs" variant="ghost" data-testid="cola-filtros-abrir">
            {t('admin.bateria.cola.filtros.abrir')}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 space-y-4 p-4" data-testid="cola-filtros-panel">
          <div className="space-y-2">
            <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('admin.bateria.cola.filtros.panel.queRevisar')}
            </p>
            <OpcionRevisar
              valor="pendientes"
              actual={borrador.revisar}
              etiqueta={t('admin.bateria.cola.filtros.panel.pendientes', {n: pendientes})}
              onElegir={(revisar) => setBorrador({...borrador, revisar})}
            />
            <OpcionRevisar
              valor="dejadas"
              actual={borrador.revisar}
              etiqueta={t('admin.bateria.cola.filtros.panel.dejadas', {n: dejadas})}
              disabled={soloPedido}
              motivo={t('admin.bateria.cola.filtros.panel.dejadasBloqueadas')}
              onElegir={(revisar) => setBorrador({...borrador, revisar})}
            />
            <OpcionRevisar
              valor="todo"
              actual={borrador.revisar}
              etiqueta={
                resumenTodo
                  ? t('admin.bateria.cola.filtros.panel.todo', {n: resumenTodo.total})
                  : t('admin.bateria.cola.filtros.panel.todoContando')
              }
              onElegir={(revisar) => setBorrador({...borrador, revisar})}
            />
          </div>

          {/* Fuera de «Todos los ítems» las casillas se apagan con su porqué en vez de ocultarse. */}
          <div className="space-y-2">
            <p className="flex items-center gap-1.5 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('admin.bateria.cola.filtros.panel.estado')}
              <HelpIcon
                label={t('common.help.iconLabelFor', {campo: t('admin.bateria.cola.filtros.panel.estado')})}
                testId="cola-filtros-estado-ayuda">
                {t('admin.bateria.cola.filtros.panel.estadoAyuda')}
              </HelpIcon>
            </p>
            {ESTADOS_FILTRO.map((estado) => (
              <label
                key={estado}
                className={cn(
                  'flex items-start gap-2 text-sm',
                  borrador.revisar === 'pendientes' ? 'text-muted-foreground' : 'text-foreground'
                )}
                data-testid={`cola-filtros-estado-${estado}`}>
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={borrador.estados.includes(estado)}
                  disabled={borrador.revisar === 'pendientes'}
                  onChange={() => setBorrador({...borrador, estados: alternarEstado(borrador.estados, estado)})}
                />
                <span>{t(`admin.bateria.cola.filtros.panel.estadoOpcion.${estado}`)}</span>
              </label>
            ))}
          </div>

          <div className="space-y-2">
            <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('admin.bateria.cola.filtros.panel.personas')}
            </p>
            <PaquetesMultiSelect
              paquetes={paquetes}
              selectedIds={borrador.paqueteIds}
              onChange={(ids) => setBorrador({...borrador, paqueteIds: ids})}
              testId="cola-filtros-personas-combobox"
            />
            {borrador.paqueteIds.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('admin.bateria.cola.filtros.todoElLote')}</p>
            ) : (
              <p className="text-sm text-foreground" data-testid="cola-filtros-personas">
                {borrador.paqueteIds.length === 1
                  ? // El borrador puede nombrar a otra persona que `sujeto`, que refleja el filtro aplicado.
                    (paquetes.find((p) => p.id === borrador.paqueteIds[0])?.employee_name ?? sujeto)
                  : t('admin.bateria.cola.filtros.personas', {n: borrador.paqueteIds.length})}
                <button
                  type="button"
                  data-testid="cola-filtros-personas-quitar"
                  aria-label={t('admin.bateria.cola.filtros.quitarPersonas')}
                  className="ml-1.5 text-muted-foreground hover:text-foreground"
                  onClick={() => setBorrador({...borrador, paqueteIds: []})}>
                  ✕
                </button>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <p className="flex items-center gap-1.5 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('admin.bateria.cola.filtros.panel.confianza')}
              <HelpIcon
                label={t('common.help.iconLabelFor', {campo: t('admin.bateria.cola.filtros.panel.confianza')})}
                testId="cola-filtros-confianza-ayuda">
                {t('admin.bateria.cola.filtros.panel.confianzaAyuda')}
              </HelpIcon>
            </p>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <Switch
                checked={borrador.confianzaMax !== null}
                data-testid="cola-filtros-confianza-switch"
                onCheckedChange={(encendido) => {
                  setPct(String(PCT_POR_DEFECTO))
                  setBorrador({...borrador, confianzaMax: encendido ? PCT_POR_DEFECTO / 100 : null})
                }}
              />
              {t('admin.bateria.cola.filtros.panel.soloPocaConfianza')}
            </label>
            {borrador.confianzaMax !== null && (
              <NumberField
                id="cola-filtros-confianza"
                label={t('admin.bateria.cola.filtros.panel.porcentaje')}
                value={pct}
                min={1}
                max={100}
                onChange={setPct}
                onCommit={(valor) => valor !== null && setBorrador({...borrador, confianzaMax: valor / 100})}
              />
            )}
          </div>

          <div className="flex items-center justify-between gap-2 pt-1">
            <Button
              size="sm"
              variant="ghost"
              data-testid="cola-filtros-limpiar"
              onClick={() =>
                aplicar({
                  ...filtros,
                  paqueteIds: [],
                  revisar: 'pendientes',
                  confianzaMax: null,
                  solo: null,
                  estados: [],
                })
              }>
              {t('admin.bateria.cola.filtros.panel.limpiar')}
            </Button>
            <Button size="sm" data-testid="cola-filtros-aplicar" onClick={() => aplicar(borrador)}>
              {t('admin.bateria.cola.filtros.panel.aplicar')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
